import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';
import i18n from '@/utils/i18n';
import React, { useEffect, useRef } from 'react';
import {
  Button,
  Icons,
  Separator,
  Typography,
  XStack,
} from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_SPRING_CONFIG_INTRO } from '@experiences/blocs/utils/animations';
import { ORDER_GUIDE_PANEL_PORTAL_HOST } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/order-guides-list-constants';
import { Portal } from '@gorhom/portal';
import { shadow } from '@/styles';
import { View } from 'react-native';

const HIDDEN_OFFSET = 100;
const TOOLBAR_HEIGHT = 50;
const DELETE_ICON_SIZE = 16;

interface OrderGuideSelectionToolbarProps {
  onDelete: () => void;
  selectedCount: number;
}

export const OrderGuideSelectionToolbar: React.FC<
  OrderGuideSelectionToolbarProps
> = ({ onDelete, selectedCount }) => {
  const [styles, theme] = useStyles();
  // number synced to the UI thread so the animation runs natively
  // without re-renders, the main driver for the animation
  const translateY = useSharedValue(HIDDEN_OFFSET);
  const isVisible = selectedCount > 0;

  useEffect(() => {
    translateY.value = withSpring(
      isVisible ? 0 : HIDDEN_OFFSET,
      DEFAULT_SPRING_CONFIG_INTRO,
    );
  }, [isVisible, translateY]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }));

  // Hold the last positive count so the label doesn't flash "0 items" on exit.
  const displayCountRef = useRef(selectedCount);
  if (isVisible) {
    displayCountRef.current = selectedCount;
  }
  const displayCount = displayCountRef.current;

  const label =
    displayCount === 1
      ? i18n.t('sales_portal.order_guides_list.selection.item_count')
      : i18n.t('sales_portal.order_guides_list.selection.item_count_plural');

  const deleteLabel = i18n.t('sales_portal.order_guides_list.selection.delete');

  return (
    <Portal hostName={ORDER_GUIDE_PANEL_PORTAL_HOST}>
      <Animated.View
        pointerEvents="box-none"
        style={[styles.container, animatedStyle]}
      >
        <View style={styles.shadowContainer}>
          <View style={styles.content}>
            <XStack
              alignItems="center"
              paddingHorizontal={theme.sk.spacing.spaceSMedium}
            >
              <Typography bold color={theme.sk.color.text.primary} size="$sm">
                {displayCount}
              </Typography>
              <Typography color={theme.sk.color.text.secondary} size="$sm">
                {' ' + label}
              </Typography>
            </XStack>
            <Separator vertical />
            <Button
              accessibilityLabel={deleteLabel}
              icon={
                <Icons.Trash
                  color={theme.sk.color.icon.negative.primary}
                  size={DELETE_ICON_SIZE}
                />
              }
              onPress={onDelete}
              size="$sm"
              variant="tertiary"
            >
              <Typography
                color={theme.sk.color.text.error}
                fontWeight="$medium"
                size="$sm"
              >
                {deleteLabel}
              </Typography>
            </Button>
          </View>
        </View>
      </Animated.View>
    </Portal>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    alignItems: 'center',
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
  },
  content: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusLarge,
    borderWidth: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceMedium,
    height: TOOLBAR_HEIGHT,
    padding: theme.sk.spacing.spaceSmall,
  },
  shadowContainer: {
    borderRadius: theme.sk.radii.radiusLarge,
    marginBottom: theme.sk.spacing.spaceLarge,
    overflow: 'hidden',
    ...shadow({
      opacity: 0.12,
      radius: 24,
      x: 0,
      y: 12,
    }),
  },
}));
